import React, { useState, useEffect } from 'react';
import { useUser } from '../../context/UserContext';
import { discountCodesAPI } from '../../services/api';
import DiscountCodeForm from './DiscountCodeForm';
import '../admin/AdminComponents.css';

const DiscountCodesManagement = () => {
  const { hasPermission } = useUser();
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingCode, setEditingCode] = useState(null);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    loadCodes();
  }, []);

  const loadCodes = async () => {
    try {
      setLoading(true);
      const response = await discountCodesAPI.getAll();
      if (response.success) {
        setCodes(response.data || []);
      }
    } catch (error) {
      console.error('Error loading discount codes:', error);
      alert(error.message || 'Failed to load discount codes');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (code) => {
    setEditingCode(code);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this discount code?')) {
      try {
        await discountCodesAPI.delete(id);
        loadCodes();
      } catch (error) {
        alert(error.message || 'Failed to delete discount code');
      }
    }
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingCode(null);
    loadCodes();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const isCodeValid = (code) => {
    if (!code.active) return false;
    const now = new Date();
    if (code.valid_from && new Date(code.valid_from) > now) return false;
    if (code.valid_until && new Date(code.valid_until) < now) return false;
    if (code.max_use_count && code.use_count >= code.max_use_count) return false;
    return true;
  };

  if (loading) {
    return (
      <div className="admin-section">
        <div className="loading-message">Loading discount codes...</div>
      </div>
    );
  }
  return (
    <div className="admin-section">
      <div className="admin-section-header">
        <h2 className="admin-section-title">Discount Codes</h2>
        <div className="admin-section-actions">
          <button
            className="admin-button primary"
            onClick={() => {
              setEditingCode(null);
              setShowForm(true);
            }}
          >
            + Add Discount Code
          </button>
        </div>
      </div>
      {showForm && (
        <DiscountCodeForm
          code={editingCode}
          onClose={handleFormClose}
        />
      )}
      {codes.length === 0 ? (
        <div className="admin-empty-state">
          <p>No discount codes found. Create your first discount code to get started.</p>
        </div>
      ) : (
        <div className="admin-table-wrapper">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Type</th>
                <th>Value</th>
                <th>Valid From</th>
                <th>Valid Until</th>
                <th>Uses</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {codes.map((code) => (
                <tr key={code.id}>
                  <td>
                    <strong>{code.code}</strong>
                    {code.description && (
                      <div className="admin-description">{code.description}</div>
                    )}
                  </td>
                  <td>{code.discount_type === 'percentage' ? 'Percentage' : 'Fixed'}</td>
                  <td>
                    {code.discount_type === 'percentage'
                      ? `${code.discount_value}%`
                      : `$${parseFloat(code.discount_value).toFixed(2)}`}
                    {code.min_purchase_amount > 0 && (
                      <div className="admin-description">
                        Min: ${parseFloat(code.min_purchase_amount).toFixed(2)}
                      </div>
                    )}
                    {code.max_discount_amount && (
                      <div className="admin-description">
                        Max: ${parseFloat(code.max_discount_amount).toFixed(2)}
                      </div>
                    )}
                  </td>
                  <td>{formatDate(code.valid_from)}</td>
                  <td>{formatDate(code.valid_until)}</td>
                  <td>
                    {code.use_count}
                    {code.max_use_count && ` / ${code.max_use_count}`}
                  </td>
                  <td>
                    <span className={`admin-status ${isCodeValid(code) ? 'active' : 'inactive'}`}>
                      {isCodeValid(code) ? 'Active' : 'Inactive'}
                    </span>
                  </td>

                  <td>
                    <div className="admin-table-actions">
                      <button
                        className="admin-icon-button"
                        onClick={() => handleEdit(code)}
                        title="Edit"
                      >
                        ✎
                      </button>

                      <button
                        className="admin-icon-button danger"
                        onClick={() => handleDelete(code.id)}
                        title="Delete"
                      >
                        ×
                      </button>

                    </div>

                  </td>

                </tr>

              ))}
            </tbody>

          </table>

        </div>

      )}
    </div>

  );
};

export default DiscountCodesManagement;

